// Nastavení firmy (SPEC.md 2.10 Organizace, Buddy, Data): GET a PATCH /v1/org, žádost o výmaz.
// Chyby se vracejí jako klíč i18n, překládá je až view.
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import type { DeleteRequestInput, OrgSettingsResponse, UpdateOrgInput } from '@wac-buddy/shared'
import { api, ApiClientError } from '@/api/client'

export type OrgStatus = 'idle' | 'loading' | 'ready' | 'error'
export type OrgActionResult = { ok: true } | { ok: false; errorKey: string; details?: unknown }

function errorKeyOf(e: unknown, fallback: string): string {
  if (!(e instanceof ApiClientError)) return 'common.network'
  if (e.code === 'forbidden') return 'settings.ownerOnly'
  return fallback
}

export const useOrgStore = defineStore('org', () => {
  const settings = ref<OrgSettingsResponse | null>(null)
  const status = ref<OrgStatus>('idle')
  const saving = ref(false)

  const isOwner = computed(() => settings.value?.role === 'owner')

  async function load(): Promise<void> {
    status.value = 'loading'
    try {
      settings.value = await api<OrgSettingsResponse>('/org')
      status.value = 'ready'
    } catch {
      status.value = 'error'
    }
  }

  /** Částečná změna nastavení firmy (jen majitel). */
  async function save(input: UpdateOrgInput): Promise<OrgActionResult> {
    saving.value = true
    try {
      settings.value = await api<OrgSettingsResponse>('/org', {
        method: 'PATCH',
        body: JSON.stringify(input),
      })
      return { ok: true }
    } catch (e) {
      return {
        ok: false,
        errorKey: errorKeyOf(e, 'common.error'),
        details: e instanceof ApiClientError ? e.details : undefined,
      }
    } finally {
      saving.value = false
    }
  }

  async function requestDelete(input: DeleteRequestInput): Promise<OrgActionResult> {
    saving.value = true
    try {
      settings.value = await api<OrgSettingsResponse>('/org/delete-request', {
        method: 'POST',
        body: JSON.stringify(input),
      })
      return { ok: true }
    } catch (e) {
      const key =
        e instanceof ApiClientError && e.code === 'validation'
          ? 'settings.data.delete.errors.name'
          : e instanceof ApiClientError && e.code === 'conflict'
            ? 'settings.data.delete.errors.already'
            : errorKeyOf(e, 'common.error')
      return { ok: false, errorKey: key }
    } finally {
      saving.value = false
    }
  }

  async function cancelDelete(): Promise<OrgActionResult> {
    saving.value = true
    try {
      settings.value = await api<OrgSettingsResponse>('/org/delete-request', { method: 'DELETE' })
      return { ok: true }
    } catch (e) {
      return { ok: false, errorKey: errorKeyOf(e, 'common.error') }
    } finally {
      saving.value = false
    }
  }

  function invalidate(): void {
    settings.value = null
    status.value = 'idle'
  }

  return { settings, status, saving, isOwner, load, save, requestDelete, cancelDelete, invalidate }
})
